import * as THREE from 'three';

import {MobileHud} from '../hud';
import {PerspectiveCamera} from '../camera';

let zee = new THREE.Vector3(0, 0, 1);
let euler = new THREE.Euler();
let q0 = new THREE.Quaternion();
let q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));

function setObjectQuaternion(quaternion: THREE.Quaternion, alpha: number, beta: number, gamma: number, orient: number) {
    euler.set(beta, alpha, -gamma, 'YXZ');
    quaternion.setFromEuler(euler);
    quaternion.multiply(q1);
    quaternion.multiply(q0.setFromAxisAngle(zee, -orient));
}

interface Moving {
    start: {
        position: THREE.Vector3,
        angle: THREE.Vector2,
    },
    end: {
        position: THREE.Vector3,
        angle: THREE.Vector2,
    },
    duration: number,
    elapsed: number,
}


export class SimpleOrientation {
    object: THREE.Camera;
    enabled: boolean;
    deviceOrientation?: DeviceOrientationEvent;
    screenOrientation: number;
    alphaOffset: number;

    _onDeviceOrientationChange: ((e: DeviceOrientationEvent) => void);
    _onScreenOrientationChange: (() => void);

    constructor(object: THREE.Camera) {
        this.object = object;
        this.object.rotation.reorder('YXZ');
        this.enabled = true;
        this.screenOrientation = 0;
        this.alphaOffset = 0;

        this._onScreenOrientationChange = () => {
            this.screenOrientation = window.orientation as unknown as number || 0;
        };

        this._onDeviceOrientationChange = (e: DeviceOrientationEvent) => {
            this.deviceOrientation = e;
        }

        this.connect();
    }

    connect() {
        this._onScreenOrientationChange();
        window.addEventListener('orientationchange', this._onScreenOrientationChange, false);
        window.addEventListener('deviceorientation', this._onDeviceOrientationChange, false);
        this.enabled = true;
    }

    disconnect() {
        window.removeEventListener('orientationchange', this._onScreenOrientationChange, false);
        window.removeEventListener('deviceorientation', this._onDeviceOrientationChange, false);
        this.enabled = false;
    }

    update() {
        if (!this.enabled) {
            return;
        }

        let device = this.deviceOrientation;
        if (device !== undefined) {
            let alpha = device.alpha ? THREE.MathUtils.degToRad(device.alpha) + this.alphaOffset : 0;
            let beta = device.beta ? THREE.MathUtils.degToRad(device.beta) : 0;
            let gamma = device.gamma ? THREE.MathUtils.degToRad(device.gamma) : 0;
            let orient = this.screenOrientation ? THREE.MathUtils.degToRad(this.screenOrientation) : 0;
            setObjectQuaternion(this.object.quaternion, alpha, beta, gamma, orient);
        }

    }

    getVectors(): { direction: THREE.Vector3, up: THREE.Vector3 } {
        this.object.updateMatrix();
        this.object.updateMatrixWorld();

        // return {
        //     direction: new THREE.Vector3(1, 0, 0),
        //     up: new THREE.Vector3(0, 1, 0),
        // };

        let direction = new THREE.Vector3(0, 0, 0.5)
            .unproject(this.object)
            .sub(this.object.position)
            .normalize();

        let up = new THREE.Vector3(0, 10, 0.5)
            .unproject(this.object)
            .sub(this.object.position)
            .sub(direction)
            .multiplyScalar(-1)
            .normalize();

        return {
            direction: direction,
            up: up,
        };
    }

    dispose() {
        this.disconnect();
    }
}

export class Orientation {
    object: THREE.Camera;
    hud: MobileHud;
    enabled: boolean;
    direction: THREE.Vector2;
    speed: number;
    sensitivity: number;
    angleOffset: THREE.Vector2;
    deviceCamera: PerspectiveCamera;
    orientationControls: SimpleOrientation;
    pointerId?: number;
    moving?: Moving;

    _onPointerDown: ((event: PointerEvent) => void);
    _onPointerUp: ((event: PointerEvent) => void);
    _onPointerMove: ((event: PointerEvent) => void);

    constructor(object: THREE.Camera, hud: MobileHud) {
        this.object = object;
        this.hud = hud;
        this.enabled = true;
        this.direction = new THREE.Vector2();
        this.speed = 0.5;
        this.sensitivity = 0.005;
        this.angleOffset = new THREE.Vector2();
        this.deviceCamera = new PerspectiveCamera(70, 16/9, 0.1, 100);
        this.orientationControls = new SimpleOrientation(this.deviceCamera);

        this._onPointerDown = (e) => this.onPointerDown(e);
        this._onPointerUp = (e) => this.onPointerUp(e);
        this._onPointerMove = (e) => this.onPointerMove(e);

        this.connect();
    }

    onPointerDown(e: PointerEvent) {
        if (this.hud.accept(e)) {
            this.pointerId = e.pointerId;
        }
        this.direction = this.hud.onPointerDown(e);
    }

    onPointerUp(e: PointerEvent) {
        if (e.pointerId === this.pointerId) {
            this.pointerId = undefined;
        }
        this.direction = this.hud.onPointerUp(e);
    }

    onPointerMove(e: PointerEvent) {
        if (e.pointerId === this.pointerId) {
            this.angleOffset.x += e.movementX * this.sensitivity;
            this.angleOffset.y += e.movementY * this.sensitivity;
        }
        this.direction = this.hud.onPointerMove(e);
    }

    connect() {
        this.orientationControls.connect();
        window.addEventListener('pointerdown', this._onPointerDown, false);
        window.addEventListener('pointerup', this._onPointerUp, false);
        window.addEventListener('pointermove', this._onPointerMove, false);
        this.enabled = true;
    }

    disconnect() {
        this.orientationControls.disconnect();
        window.removeEventListener('pointerdown', this._onPointerDown, false);
        window.removeEventListener('pointerup', this._onPointerUp, false);
        window.removeEventListener('pointermove', this._onPointerMove, false);
        this.enabled = false;
    }

    update() {

        if (this.enabled === false) return;

        this.orientationControls.update();

        this.updateMovingIfNecessary(20);

        // Move camera based on joystick
        this.object.updateMatrix();
        this.object.updateMatrixWorld();
        this.object.matrixWorldInverse.copy(this.object.matrixWorld);
        this.object.matrixWorldInverse.invert();

        let forward = new THREE.Vector3(0.0, 0.0, 0.5)
            .unproject(this.object)
            .sub(this.object.position)
            .normalize();

        let right = new THREE.Vector3(0, 1, 0)
            .cross(forward)
            .multiplyScalar(-this.speed * this.direction.x);

        forward
            .multiplyScalar(this.speed * this.direction.y)
            .add(right);

        this.object.position.add(forward);

        // Apply touch controls
        this.orientationControls.update();

        let direction = this.getDirection();
        this.object.lookAt(direction.add(this.object.position));

    }

    getDirection(angleOffset?: THREE.Vector2) {
        if (angleOffset === undefined) {
            angleOffset = this.angleOffset;
        }

        let vectors = this.orientationControls.getVectors();
        vectors.direction.applyAxisAngle(new THREE.Vector3(0, 1, 0), angleOffset.x);

        let horizontalAxis = new THREE.Vector3(0, 1, 0).cross(vectors.direction).normalize();
        vectors.direction.applyAxisAngle(horizontalAxis, -angleOffset.y);

        return vectors.direction;
    }

    dispose() {
        this.disconnect();
    }

    moveTo(position: THREE.Vector3, target: THREE.Vector3, duration: number) {

        // This is hard
        // EDIT: This is not hard, this is fucking magic
        // Thank you Terry-san ＼(＾▽＾)／
        let currentDirection = this.getDirection(new THREE.Vector2()).normalize();
        let currentAlpha = -Math.atan2(currentDirection.z, currentDirection.x);
        let currentBeta = Math.asin(currentDirection.y);

        let targetDirection = target.clone().sub(position).normalize();
        let targetAlpha = -Math.atan2(targetDirection.z, targetDirection.x);
        let targetBeta = Math.asin(targetDirection.y);

        let targetOffset = new THREE.Vector2(
            targetAlpha - currentAlpha,
            targetBeta - currentBeta,
        );

        this.angleOffset.x %= (2 * Math.PI);
        this.angleOffset.y %= (2 * Math.PI);
        targetOffset.x %= (2 * Math.PI);
        targetOffset.y %= (2 * Math.PI);

        if (targetOffset.x - this.angleOffset.x > Math.PI) {
            targetOffset.x -= 2 * Math.PI;
        }

        if (targetOffset.y - this.angleOffset.y > Math.PI) {
            targetOffset.y -= 2 * Math.PI;
        }

        this.moving = {
            start: {
                position: this.object.position.clone(),
                angle: this.angleOffset.clone()
            },
            end: {
                position: position.clone(),
                angle: targetOffset
            },
            elapsed: 0,
            duration: duration,
        };

    }

    updateMovingIfNecessary(time: number) {
        if (this.moving === undefined) {
            return;
        }

        this.moving.elapsed += time;

        let finished = false;
        let t = this.moving.elapsed / this.moving.duration;

        this.object.position.copy(
            this.moving.start.position.clone().multiplyScalar(1-t).clone()
                .add(this.moving.end.position.clone().multiplyScalar(t)));

        this.angleOffset.copy(
            this.moving.start.angle.clone().multiplyScalar(1-t).clone()
                .add(this.moving.end.angle.clone().multiplyScalar(t)));

        if (t > 1) {
            t = 1;
            finished = true;
        }

        if (finished) {
            this.moving = undefined;
        }

    }
}

export class NaiveOrientation extends Orientation {
    getDirection(angleOffset?: THREE.Vector2) {
        if (angleOffset === undefined) {
            angleOffset = this.angleOffset;
        }

        let vectors = {
            direction: new THREE.Vector3(0, 0, 1),
            up: new THREE.Vector3(0, 1, 0),
        };
        vectors.direction.applyAxisAngle(new THREE.Vector3(0, 1, 0), angleOffset.x);

        let horizontalAxis = new THREE.Vector3(0, 1, 0).cross(vectors.direction).normalize();
        vectors.direction.applyAxisAngle(horizontalAxis, -angleOffset.y);

        return vectors.direction;
    }
}
